import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import './AccessibilitySettings.css';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mr', name: 'Marathi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'pa', name: 'Punjabi' },
];

const PLATFORMS = [
  { id: 'swiggy', name: 'Swiggy', icon: '🍔', color: '#fc8019' },
  { id: 'zomato', name: 'Zomato', icon: '🍕', color: '#e23744' },
  { id: 'blinkit', name: 'Blinkit', icon: '⚡', color: '#f8cb46' },
  { id: 'bigbasket', name: 'BigBasket', icon: '🛒', color: '#84c225' },
  { id: 'amazon', name: 'Amazon', icon: '📦', color: '#ff9900' },
  { id: 'flipkart', name: 'Flipkart', icon: '🛍️', color: '#2874f0' },
];

const AccessibilitySettings = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    autoSendInstructions: false,
    autoSendDelay: 5,
    notifyBeforeSending: true,
    enabledPlatforms: ['swiggy', 'zomato', 'blinkit', 'bigbasket'],
    preferredRiderLanguage: 'hi'
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [isAuthenticated, navigate]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/accessibility/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/accessibility/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error(data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const togglePlatform = (platformId) => {
    setSettings(prev => {
      const platforms = prev.enabledPlatforms || [];
      if (platforms.includes(platformId)) {
        return {
          ...prev,
          enabledPlatforms: platforms.filter(p => p !== platformId)
        };
      } else {
        return {
          ...prev,
          enabledPlatforms: [...platforms, platformId]
        };
      }
    });
  };

  if (loading) {
    return (
      <div className="accessibility-settings-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="accessibility-settings-page">
      <header className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Accessibility Settings</h1>
      </header>

      <div className="settings-container">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-icon">🤖</div>
          <h2>Auto-Detect Rider Numbers</h2>
          <p>
            Enable accessibility service to automatically detect delivery rider's
            phone numbers from app notifications and send your instructions instantly.
          </p>
        </div>

        {/* Main Toggle */}
        <div className="setting-card main-toggle">
          <div className="setting-info">
            <div className="setting-icon">📱</div>
            <div className="setting-text">
              <h3>Enable Accessibility Service</h3>
              <p>Allow DeliveryBridge to read delivery app notifications</p>
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={e => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        {settings.enabled && (
          <>
            {/* Setup Instructions */}
            <div className="setup-instructions">
              <h3>📋 Setup Instructions</h3>
              <ol>
                <li>
                  <span className="step-number">1</span>
                  <div>
                    <strong>Open Android Settings</strong>
                    <p>Go to Settings → Accessibility → Installed Services</p>
                  </div>
                </li>
                <li>
                  <span className="step-number">2</span>
                  <div>
                    <strong>Find DeliveryBridge</strong>
                    <p>Look for "DeliveryBridge" in the list of accessibility services</p>
                  </div>
                </li>
                <li>
                  <span className="step-number">3</span>
                  <div>
                    <strong>Enable the Service</strong>
                    <p>Toggle ON and grant notification access permission</p>
                  </div>
                </li>
              </ol>
              <button className="btn-open-settings">
                Open Accessibility Settings
              </button>
            </div>

            {/* Auto-Send Toggle */}
            <div className="setting-card">
              <div className="setting-info">
                <div className="setting-icon">🚀</div>
                <div className="setting-text">
                  <h3>Auto-Send Instructions</h3>
                  <p>Automatically send instructions when rider is detected</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.autoSendInstructions}
                  onChange={e => setSettings(prev => ({
                    ...prev,
                    autoSendInstructions: e.target.checked
                  }))}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {settings.autoSendInstructions && (
              <>
                {/* Auto-Send Delay */}
                <div className="setting-card">
                  <div className="setting-info">
                    <div className="setting-icon">⏱️</div>
                    <div className="setting-text">
                      <h3>Auto-Send Delay</h3>
                      <p>Wait before sending (gives you time to cancel)</p>
                    </div>
                  </div>
                  <select
                    value={settings.autoSendDelay}
                    onChange={e => setSettings(prev => ({
                      ...prev,
                      autoSendDelay: parseInt(e.target.value)
                    }))}
                    className="delay-select"
                  >
                    <option value={0}>Immediately</option>
                    <option value={3}>3 seconds</option>
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                  </select>
                </div>

                {/* Notify Before Sending */}
                <div className="setting-card">
                  <div className="setting-info">
                    <div className="setting-icon">🔔</div>
                    <div className="setting-text">
                      <h3>Notify Before Sending</h3>
                      <p>Show notification with option to cancel</p>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.notifyBeforeSending}
                      onChange={e => setSettings(prev => ({
                        ...prev,
                        notifyBeforeSending: e.target.checked
                      }))}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </>
            )}

            {/* Preferred Rider Language */}
            <div className="setting-card">
              <div className="setting-info">
                <div className="setting-icon">🌐</div>
                <div className="setting-text">
                  <h3>Default Rider Language</h3>
                  <p>Instructions will be translated to this language</p>
                </div>
              </div>
              <select
                value={settings.preferredRiderLanguage}
                onChange={e => setSettings(prev => ({
                  ...prev,
                  preferredRiderLanguage: e.target.value
                }))}
                className="language-select"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>

            {/* Enabled Platforms */}
            <div className="setting-section">
              <h3>
                <span className="section-icon">🛵</span>
                Monitor These Apps
              </h3>
              <p className="section-desc">
                Select which delivery apps to monitor for rider notifications
              </p>
              <div className="platforms-grid">
                {PLATFORMS.map(platform => (
                  <button
                    key={platform.id}
                    className={`platform-card ${settings.enabledPlatforms?.includes(platform.id) ? 'selected' : ''}`}
                    onClick={() => togglePlatform(platform.id)}
                    style={settings.enabledPlatforms?.includes(platform.id) ? {
                      borderColor: platform.color,
                      background: `${platform.color}15`
                    } : {}}
                  >
                    <span className="platform-icon">{platform.icon}</span>
                    <span className="platform-name">{platform.name}</span>
                    {settings.enabledPlatforms?.includes(platform.id) && (
                      <span className="check-mark" style={{ color: platform.color }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="info-section">
              <h3>🔒 Privacy & Security</h3>
              <ul>
                <li>We only read notifications from selected delivery apps</li>
                <li>Phone numbers are extracted locally on your device</li>
                <li>No notification content is stored or sent to servers</li>
                <li>You can disable this feature anytime</li>
              </ul>
            </div>
          </>
        )}

        {/* Save Button */}
        <button
          className="btn-save"
          onClick={saveSettings}
          disabled={saving}
        >
          {saving ? '⏳ Saving...' : '💾 Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AccessibilitySettings;
